import { makeAutoObservable, runInAction } from "mobx";
import {
  BENCHMARKING_DATA,
  CATEGORY_PROGRESS,
  DASHBOARD_SUMMARY,
  FRAMEWORK_COMPLETION,
  METRICS_LIST,
  MOCK_REPORTS,
  getFrameworkCompletionSummary,
} from "../utils/esg-dashboard-mocks";

const MOCK_LOAD_DELAY_MS = 400;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class EsgDashboardStore {
  initialized = false;
  loading = false;
  refreshing = false;

  reports = [];
  selectedOrgId = "";
  selectedReportId = "";
  consolidatedMode = false;
  consolidatedFramework = "";
  dashboardYear = new Date().getFullYear();

  summary = DASHBOARD_SUMMARY;
  categoryProgress = CATEGORY_PROGRESS;
  benchmarking = BENCHMARKING_DATA;
  frameworkCompletion = FRAMEWORK_COMPLETION;
  completionSummary = getFrameworkCompletionSummary();
  metricsList = METRICS_LIST;

  metricsModalOpen = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
  }

  ensureData = async () => {
    if (this.initialized || this.loading) return;
    this.loading = true;
    await sleep(MOCK_LOAD_DELAY_MS);
    runInAction(() => {
      this.reports = MOCK_REPORTS;
      this.summary = DASHBOARD_SUMMARY;
      this.categoryProgress = CATEGORY_PROGRESS;
      this.benchmarking = BENCHMARKING_DATA;
      this.frameworkCompletion = FRAMEWORK_COMPLETION;
      this.completionSummary = getFrameworkCompletionSummary();
      this.metricsList = METRICS_LIST;
      this.selectedOrgId = this.selectedOrgId || "";
      this.selectedReportId =
        this.selectedReportId || this.reports[0]?.id || "";
      this.dashboardYear = this.availableYears[0] || this.dashboardYear;
      this.initialized = true;
      this.loading = false;
    });
  };

  refreshDashboard = async () => {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loading = true;
    await sleep(MOCK_LOAD_DELAY_MS);
    runInAction(() => {
      this.summary = {
        ...DASHBOARD_SUMMARY,
        lastDataUpdate: new Date().toISOString(),
      };
      this.refreshing = false;
      this.loading = false;
    });
  };

  setSelectedReportId = (reportId) => {
    this.selectedReportId = reportId || "";
  };

  setSelectedOrgId = (orgId) => {
    this.selectedOrgId = orgId || "";
    if (this.selectedReportId) {
      const available = this.reports.filter(
        (report) => !this.selectedOrgId || report.orgId === this.selectedOrgId,
      );
      if (!available.length) return;
      const stillVisible = available.some(
        (report) => report.id === this.selectedReportId,
      );
      if (!stillVisible) {
        this.selectedReportId = available[0]?.id || "";
      }
    }
  };

  setConsolidatedMode = (value) => {
    this.consolidatedMode = Boolean(value);
    if (this.consolidatedMode) {
      this.selectedReportId = "";
    }
  };

  setConsolidatedFramework = (framework) => {
    this.consolidatedFramework = framework || "";
  };

  setDashboardYear = (year) => {
    const next = Number(year);
    if (!Number.isFinite(next)) return;
    this.dashboardYear = next;
  };

  openMetricsModal = () => {
    this.metricsModalOpen = true;
  };

  closeMetricsModal = () => {
    this.metricsModalOpen = false;
  };

  get filteredReports() {
    if (!this.selectedOrgId) return this.reports;
    const matches = this.reports.filter(
      (report) => report.orgId === this.selectedOrgId,
    );
    return matches.length ? matches : this.reports;
  }

  get selectedReport() {
    if (!this.selectedReportId) return null;
    return this.reports.find((report) => report.id === this.selectedReportId);
  }

  get availableFrameworks() {
    const target = this.filteredReports.length
      ? this.filteredReports
      : this.reports;
    return Array.from(new Set(target.map((report) => report.framework)));
  }

  get availableYears() {
    const years = this.reports
      .map((report) => report.reportYear)
      .filter((year) => Number.isFinite(year));
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }
}

export const esgDashboardStore = new EsgDashboardStore();
